import { forwardRef, useRef, useState } from 'react';
import { Button, useMergedRefs } from '@fluentui/react-components';
import { DatePicker, type DatePickerProps } from '@fluentui/react-datepicker-compat';
import { CalendarMonthRegular, DismissRegular } from '@fluentui/react-icons';
import { formatCalendarDate, parseCalendarDate } from '@shared/utils/date';

export type TCalendarDatePickerProps = Omit<
  DatePickerProps,
  'formatDate' | 'parseDateFromString' | 'onChange' | 'onValidationResult' | 'calendar'
> & {
  clearable?: boolean;
  clearLabel?: string;
  onInvalidInputChange?: (invalid: boolean) => void;
};

const CalendarDatePicker = forwardRef<HTMLInputElement, TCalendarDatePickerProps>(
  (
    {
      clearable = false,
      clearLabel = 'Clear date',
      onInvalidInputChange,
      onSelectDate,
      defaultOpen = false,
      open: controlledOpen,
      onOpenChange,
      ...props
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const mergedRef = useMergedRefs(inputRef, ref);
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const open = controlledOpen ?? isOpen;
    const handleOpenChange = (nextOpen: boolean) => {
      setIsOpen(nextOpen);
      onOpenChange?.(nextOpen);
    };

    return (
      <DatePicker
        placeholder="Select a date"
        firstDayOfWeek={1}
        showGoToToday={false}
        formatDate={formatCalendarDate}
        parseDateFromString={parseCalendarDate}
        allowTextInput
        {...props}
        ref={mergedRef}
        open={open}
        onOpenChange={handleOpenChange}
        onSelectDate={(date) => {
          onInvalidInputChange?.(false);
          onSelectDate?.(date);
        }}
        onChange={(_, data) => {
          if (!data.value || parseCalendarDate(data.value)) onInvalidInputChange?.(false);
        }}
        onValidationResult={({ error }) => onInvalidInputChange?.(Boolean(error))}
        // Fluent skips the top-level callback when the original day is selected again.
        calendar={{ onSelectDate: () => onInvalidInputChange?.(false) }}
        contentAfter={
          clearable
            ? {
                role: undefined,
                'aria-expanded': undefined,
                'aria-labelledby': undefined,
                children: (
                  <>
                    {props.value && (
                      <Button
                        type="button"
                        appearance="transparent"
                        size="small"
                        icon={<DismissRegular />}
                        aria-label={clearLabel}
                        title={clearLabel}
                        disabled={props.disabled || props.readOnly}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={(event) => {
                          event.stopPropagation();
                          // Blurring another picker can commit its value; clear this one afterwards.
                          inputRef.current?.focus();
                          handleOpenChange(false);
                          onInvalidInputChange?.(false);
                          onSelectDate?.(null);
                        }}
                      />
                    )}
                    <Button
                      type="button"
                      appearance="transparent"
                      size="small"
                      icon={<CalendarMonthRegular />}
                      aria-label="Open calendar"
                      aria-expanded={open}
                      disabled={props.disabled}
                    />
                  </>
                ),
              }
            : props.contentAfter
        }
      />
    );
  }
);
export default CalendarDatePicker;
